// the order here determines how the sections appear on the add section view
export enum WidgetId {
  BrandclubNavigation = 'Brandclub_Navigation',
  BrandHeader = 'Brand_Header',
  BrandHeaderWithImage = 'Brand_Header_With_Image',
  BrandHeaderWithVideo = 'Brand_Header_With_Video',
  BrandHeaderWithMultipleImages = 'Brand_Header_With_Multiple_Images',
  BrandHeaderWithMultipleVideos = 'Brand_Header_With_Multiple_Videos',
  EditorialHeader = 'Editorial_Header',
  ProductDetails = 'Product_Details',
  BulletPoints = 'Bullet_Points',
  EditorialTitleAndParagraph = 'Editorial_Title_and_Paragraph',
  CenterTitle = 'Center_Title',
  CenterTitleAndParagraph = 'Center_Title_and_Paragraph',
  VisualSpacer = 'Visual_Spacer',
  SixRectangleBoxes = 'Six_Rectangle_Boxes',
  CenterProductHero = 'Center_Product_Hero',
  LargeCenterVideo = 'Large_Center_Video',
  RectangleImageCarousel = 'Rectangle_Image_Carousel',
  ShortWideBanner = 'Short_Wide_Banner',
  LargeCenterImage = 'Large_Center_Image',
  LargeCenterBannerWithTiles = 'Large_Center_Banner_With_Tiles',
  MediumCenterImage = 'Medium_Center_Image',
  SideBySideImages = 'Side_by_Side_Images',
  ImageAndText = 'Image_and_Text',
  TextAndImage = 'Text_and_Image',
  SideBySideProducts = 'Side_by_Side_Products',
  ProductAndText = 'Product_and_Text',
  TextAndProduct = 'Text_and_Product',
  PopularProducts = 'Popular_Products',
  CuratedProducts = 'Curated_Products',
  ThreeSpotlightImages = 'Three_Spotlight_Images',
  TiledImages = 'Tiled_Images',
  TextCarousel = 'Text_Carousel',
  VideoCarousel = 'Video_Carousel',
  FiveShortFeatures = 'Five_Short_Features',
  ThreeDetailedFeatures = 'Three_Detailed_Features',
  SixDetailedFeatures = 'Six_Detailed_Features',
  VideoAndText = 'Video_and_Text',
  TextAndVideo = 'Text_and_Video',
  VideoAndProduct = 'Video_and_Product',
  ProductAndVideo = 'Product_and_Video',
  BrandclubRewardCarousel = 'Brandclub_Reward_Carousel',
  BrandclubProductRewardCarousel = 'Brandclub_Product_Reward_Carousel',
  BrandclubAppDownload = 'Brandclub_App_Download',
  BrandclubHowItWorks = 'Brandclub_How_It_Works',
  BrandclubCenterDownload = 'Brandclub_Center_Download',
  BrandclubVideoCarousel = 'Brandclub_Video_Carousel',
  BrandclubShopRetailers = 'Brandclub_Shop_Retailers',
  BrandclubCashBack = 'Brandclub_Cash_Back',
  BrandclubAffiliateRewards = 'Brandclub_Affiliate_Rewards',
  BrandclubSurveyRewards = 'Brandclub_Survey_Rewards',
  BrandclubTestimonialCarousel = 'Brandclub_Testimonial_Carousel',
  BrandclubGetApp = 'Brandclub_Get_App',
  BrandclubRightDownload = 'Brandclub_Right_Download',
  // widgets that cannot be added
  BrandNavigationAndSearchBar = 'Brand_Navigation_and_Search_Bar',
  SearchResults = 'Search_Results',
  BrandclubPageCards = 'Brandclub_Page_Cards',
  EditorialMoreFromCards = 'Editorial_More_From_Cards',
  BrandclubFooter = 'Brandclub_Footer',
}

export enum WidgetConfigStatus {
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
}

export enum WidgetAssetType {
  Brandclub = 'brandclub',
  Text = 'text',
  Image = 'image',
  Product = 'product',
  Video = 'video',
  Spotlight = 'spotlight',
}

export enum WidgetBuilderType {
  Text = 'text',
  Video = 'video',
  Image = 'image',
  Product = 'product',
  Category = 'category',
  Subcategory = 'subcategory',
  Link = 'link',
  Nested = 'nested',
  NumberRange = 'numberRange',
  None = 'none', // ex: standard assets
}

export interface Dimensions {
  height?: number; // pixels
  width?: number; // pixels
}

export enum InputPropsValue {
  List = 'list',
  Display = 'display',
  StacklineSKU = 'stacklineSku',
}

export enum WidgetInputComponent {
  Slider = 'slider',
  Radio = 'radio',
}

export enum WidgetPropType {
  UserInput = 'userInput',
  React = 'react',
  Redux = 'redux',
}

export interface ListLayoutProps {
  // boxes
  cropShape: 'rect' | 'round';
  previewField?: string; // field to be used in the box; pass only when props is object
  layout: string; // "3-3", "5", etc.
  maxWidth: number;
  gap: number;
  aspectRatio: {
    height: number;
    width: number;
  };
}

export type RadioOption = {
  value: string | number;
  title: string;
  subtitle: string;
};

export type WidgetPropConfig<T = any> = {
  propType: WidgetPropType;
  propName: string;
  index?: number;
  propValueConfig: {
    title?: string;
    subtitle?: string;
    placeholder?: string;
    required?: boolean; // if not present assume true
    isUserEditable: boolean;
    valueType?: InputPropsValue;
    builderInputType: WidgetBuilderType;
    maxLength?: number;
    minLength?: number;
    minDimensions?: Dimensions; // in pixels
    requiredAspectRatio?: Dimensions;
    maxDimensions?: Dimensions; // in pixels
    minFileSize?: number; // bytes
    maxFileSize?: number; // bytes
    minCount?: number;
    maxWidth?: number;
    maxCount?: number;
    cropShape?: 'rect' | 'round';
    pageTypeFilter?: string[]; // (BrandclubPageType | "Product")[]; search everything for undefined OR empty array; used with builderInputType = "link"
    titlePrefix?: string; // ex: 'Bullet Point ' in 'Bullet Point 1'
    titleFormat?: 'suffix-index'; // ex: 'Bullet Point 1',
    listLayoutProps?: ListLayoutProps;
    min?: number; // range input min
    max?: number; // range input max
    step?: number; // range input step
    prefix?: string; // range input formate prefix
    suffix?: string; // range input formate suffix
    inputComponent?: WidgetInputComponent;
    radioOptions?: RadioOption[];
  } & Record<string, any>;
  propValue?: T; // can be string, number, list, nested objects, etc.
  propsConfig?: Record<string, WidgetPropConfig>;
};

export type Widget = {
  widgetId: string; // unique, engineer (human) readable, e.g. 'POPULAR_PRODUCTS'
  status: WidgetConfigStatus;
  fixedOrder?: boolean; // if true, Widget cannot be reordered in the hierarchy (ex: header, footer)
  canBeEdited: boolean;
  canBeReordered: boolean; // Default: true
  canBeDeleted: boolean; // Default: true
  forceFullWidth?: boolean;
  displayName: string;
  shortDisplayName?: string;
  previewImageUrl?: string;
  placeholder?: string;
  // This is used to filter
  assetType: WidgetAssetType[];
  // If pageType is not present, we can treat it as unlimited
  // default, will set it regardless of page type
  // pageType will always override the default
  // If pageType set to 0, then we can treat this widget as not compatible with the page
  maxNumberOfInstancesConfig: { [pageType: string]: number } & {
    default?: number;
  };
  propsConfig: Record<string, WidgetPropConfig>;
};
